import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';

import {
    Footer,
    Header,
    Meta
} from '$partials/global';

import {
    findModuleSlides,
    getAllModules
} from '$utilities/data';

import {
    AwardsModuleBannerText,
    AwardsModuleLandingHeader,
    AwardsModuleLandingSlideNavigationBottom,
    AwardsModuleLandingSlideNavigationTop
} from '$partials/modules/individual';

import { useAnchor } from '$contexts/anchor';
import ModuleDispatcher from '$partials/modules/dispatcher';
import { linearScale } from '$utilities/scale';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const AwardsLandingPage = ({
    data,
    domain
}) => {
    // misc
    const page = data?.page;

    // references
    const slideNavRef = useRef();
    const bannerTextRef = useRef();

    // state
    const [ modules ] = useState(getAllModules(page));
    const [ slideModules ] = useState(findModuleSlides(modules));

    // hooks
    const { setAnchors, anchors } = useAnchor();

    useEffect(() => {
        if (modules) {
            setAnchors(slideModules);
        }
    }, [ modules, page, slideModules ]);

    const handleScroll = () => {
        if (window.innerWidth > 1023) {
            const bannerTextBottom = (bannerTextRef.current.offsetTop + bannerTextRef.current.offsetHeight);
            const opacityValue = linearScale(window.scrollY, (bannerTextBottom - 100), bannerTextBottom, 0, 1);

            if (slideNavRef.current) {
                slideNavRef.current.style.opacity = opacityValue;
            }
        }
    };

    // hooks
    useEffect(() => {
        const rafScroll = () => window.requestAnimationFrame(handleScroll);

        window.addEventListener('scroll', rafScroll, { passive: true });

        rafScroll();

        return () => {
            window.removeEventListener('scroll', rafScroll);
        };
    }, [
        slideNavRef.current
    ]);

    // render
    return (
        <>
            {/* meta */}
            <Meta page={page} />

            {/* main */}
            <main
                className='bg-black min-h-screen'
                id='main-content'>
                {/* container */}
                <div className='layout-container relative z-10'>
                    {/* header */}
                    <Header
                        invertTextColour={true}
                        menu={data?.navigation}
                        search={data?.search}
                        title={page.title} />
                </div>

                {/* landing header */}
                <AwardsModuleLandingHeader
                    images={page.images?.items}
                    logo={page.logo}
                    title={page.title} />

                <article>


                    <div
                        className='relative md:contents'>

                        {/* banner text */}
                        {page.bannerCopy && page.bannerTitle && (
                            <AwardsModuleBannerText
                                backgroundColor={page.bannerBackgroundColor}
                                body={page.bannerCopy}
                                ref={bannerTextRef}
                                title={page.bannerTitle}/>
                        )}

                        {/* slide navigation top */}
                        {anchors.length > 0 && (
                            <AwardsModuleLandingSlideNavigationTop
                                anchors={anchors}
                                ref={slideNavRef}/>
                        )}

                        {/* modules */}
                        {(page.modules?.items?.length > 0) && (
                            page.modules.items.map((module, idx) => {
                                if (!module) { return null; }

                                return (
                                    <ModuleDispatcher
                                        key={idx}
                                        module={module}
                                        nextTypename={page.modules.items[idx + 1]?.__typename}
                                        previousTypename={page.modules.items[idx - 1]?.__typename} />
                                );
                            })
                        )}

                        {/* slide navigation bottom */}
                        {anchors.length > 0 && (
                            <AwardsModuleLandingSlideNavigationBottom anchors={anchors} />
                        )}
                    </div>
                    <div className='layout-container flex justify-center'>
                        <a
                            className='px-10 sm:px-20 type-style-awards-heading-uppercase text-white underline'
                            href={`/year-in-sport/${page.slug}/nominees`}>
                            View all nominees
                        </a>
                    </div>
                </article>

                <div className='layout-container'>
                    {/* end content border */}
                    <div className='border-b border-white mt-60 md:mt-80 mx-10 sm:mx-20' />

                    {/* footer */}
                    <Footer
                        domain={domain}
                        invertTextColour={true}
                        menu={data?.footer}
                        title='Strava Awards' />
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    data: PropTypes.object.isRequired,
    domain: PropTypes.string
};

AwardsLandingPage.propTypes = propTypes;

export default AwardsLandingPage;
